'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2, Copy, Check, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

// Google SVG icon component
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9087c1.7018-1.5668 2.6836-3.874 2.6836-6.615z" fill="#4285F4"/>
    <path d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9087-2.2581c-.8059.54-1.8368.859-3.0477.859-2.3441 0-4.3282-1.5831-5.036-3.7104H.9574v2.3318C2.4382 15.9832 5.4818 18 9 18z" fill="#34A853"/>
    <path d="M3.964 10.71c-.18-.54-.2822-1.1168-.2822-1.71s.1023-1.17.2822-1.71V4.9582H.9574C.3477 6.1731 0 7.5477 0 9s.3477 2.8268.9574 4.0418L3.964 10.71z" fill="#FBBC05"/>
    <path d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C13.4627.8918 11.4255 0 9 0 5.4818 0 2.4382 2.0168.9574 4.9582L3.964 7.29C4.6718 5.1627 6.6559 3.5795 9 3.5795z" fill="#EA4335"/>
  </svg>
);

interface LoginFormValues {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface Props {
  onSwitchToSignup: () => void;
}

const DEMO_EMAIL = 'maya.chen@financetracker.app';
const DEMO_PASSWORD = 'Budget2026!';

export default function LoginForm({ onSwitchToSignup }: Props) {
  const router = useRouter();
  const { signIn, signInWithGoogle } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [copiedField, setCopiedField] = useState<'email' | 'password' | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormValues>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const handleCopy = async (field: 'email' | 'password') => {
    const value = field === 'email' ? DEMO_EMAIL : DEMO_PASSWORD;
    await navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleUseDemoCredentials = () => {
    setValue('email', DEMO_EMAIL);
    setValue('password', DEMO_PASSWORD);
    setAuthError('');
  };

  const onSubmit = async (data: LoginFormValues) => {
    setIsLoading(true);
    setAuthError('');
    try {
      await signIn(data.email, data.password);
      // Use full navigation to ensure server-side session cookie is read by middleware
      window.location.href = '/dashboard';
    } catch (error: any) {
      setAuthError(error?.message || 'Invalid email or password. Please try again.');
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    setAuthError('');
    try {
      await signInWithGoogle();
      // Redirect is handled by Supabase OAuth flow
    } catch (error: any) {
      setAuthError(error?.message || 'Google sign-in failed. Please try again.');
      setIsGoogleLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-foreground">Welcome back</h2>
        <p className="text-muted-foreground text-sm mt-1.5">
          Sign in to view your spending and budget overview.
        </p>
      </div>

      {/* Auth error */}
      {authError && (
        <div className="flex items-start gap-3 p-3.5 rounded-lg border text-sm animate-slide-up mb-5"
          style={{ backgroundColor: 'var(--danger-bg)', borderColor: 'var(--danger)' }}>
          <AlertCircle size={16} className="text-danger flex-shrink-0 mt-0.5" />
          <span className="text-danger">{authError}</span>
        </div>
      )}

      {/* Google Sign In */}
      <button
        type="button"
        onClick={handleGoogleSignIn}
        disabled={isGoogleLoading || isLoading}
        className="w-full flex items-center justify-center gap-3 py-2.5 px-4 rounded-lg border border-border bg-background hover:bg-muted/50 transition-colors text-sm font-medium text-foreground disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {isGoogleLoading ? (
          <Loader2 size={16} className="animate-spin" />
        ) : (
          <GoogleIcon />
        )}
        Continue with Google
      </button>

      {/* Divider */}
      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground font-medium">or sign in with email</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
        {/* Email */}
        <div>
          <label htmlFor="login-email" className="form-label">
            Email address
          </label>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            className={`form-input ${errors.email ? 'error' : ''}`}
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Enter a valid email address',
              },
            })}
          />
          {errors.email && (
            <p className="form-error">
              <AlertCircle size={12} />
              {errors.email.message}
            </p>
          )}
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="login-password" className="form-label mb-0">
              Password
            </label>
            <button
              type="button"
              className="text-xs font-medium text-primary hover:underline"
            >
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Enter your password"
              className={`form-input pr-10 ${errors.password ? 'error' : ''}`}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 6, message: 'Password must be at least 6 characters' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="form-error">
              <AlertCircle size={12} />
              {errors.password.message}
            </p>
          )}
        </div>

        {/* Remember me */}
        <div className="flex items-center gap-2.5">
          <input
            id="remember-me"
            type="checkbox"
            className="w-4 h-4 rounded border-border accent-primary"
            {...register('rememberMe')}
          />
          <label htmlFor="remember-me" className="text-sm text-muted-foreground cursor-pointer select-none">
            Keep me signed in for 30 days
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading || isGoogleLoading}
          className="btn-primary w-full py-3 text-base mt-2"
        >
          {isLoading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Signing in…
            </>
          ) : (
            'Sign In'
          )}
        </button>
      </form>

      {/* Divider */}
      <div className="flex items-center gap-3 my-6">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground font-medium">Demo credentials</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      {/* Demo credentials box */}
      <div className="rounded-xl border border-border bg-muted/50 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">Email</p>
            <p className="text-sm font-medium text-foreground truncate font-tabular">{DEMO_EMAIL}</p>
          </div>
          <button
            type="button"
            onClick={() => handleCopy('email')}
            className="ml-3 flex-shrink-0 p-1.5 rounded-md hover:bg-border transition-colors"
            aria-label="Copy email"
          >
            {copiedField === 'email' ? (
              <Check size={14} className="text-success" />
            ) : (
              <Copy size={14} className="text-muted-foreground" />
            )}
          </button>
        </div>

        <div className="h-px bg-border" />

        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">Password</p>
            <p className="text-sm font-medium text-foreground font-tabular">{DEMO_PASSWORD}</p>
          </div>
          <button
            type="button"
            onClick={() => handleCopy('password')}
            className="ml-3 flex-shrink-0 p-1.5 rounded-md hover:bg-border transition-colors"
            aria-label="Copy password"
          >
            {copiedField === 'password' ? (
              <Check size={14} className="text-success" />
            ) : (
              <Copy size={14} className="text-muted-foreground" />
            )}
          </button>
        </div>

        <button
          type="button"
          onClick={handleUseDemoCredentials}
          className="w-full text-center text-xs font-semibold text-primary hover:underline pt-1"
        >
          Autofill demo credentials →
        </button>
      </div>

      <p className="text-center text-sm text-muted-foreground mt-6">
        No account yet?{' '}
        <button
          type="button"
          onClick={onSwitchToSignup}
          className="text-primary font-semibold hover:underline"
        >
          Create one free
        </button>
      </p>
    </div>
  );
}